import { CapiProviderName } from "@next-platform/packages-ts-types";

import { type FireConversionParams } from "@/features/tracking/registry/fireConversion.types";
import appLogger from "@next-platform/client-common-core/logger/appLogger";

const registryLogger = appLogger.withTag("ConversionRegistry");

// Provider identifiers come from the shared `CapiProviderName` const
// in `@next-platform/packages-ts-types` so browser pixel registration,
// backend CAPI dispatcher keys, Secrets Manager lookups, and Ads
// Manager custom conversions all reference one stable set of strings.
export type ConversionProviderId = CapiProviderName;

/** MUST be synchronous and MUST NOT throw — the registry's try/catch is a safety net, not a contract. */
export type ConversionProviderDispatcher = (params: FireConversionParams) => void;

type RegisteredProvider = {
  id: ConversionProviderId;
  dispatch: ConversionProviderDispatcher;
};

const providers = new Map<ConversionProviderId, RegisteredProvider>();
const pendingByProvider = new Map<ConversionProviderId, FireConversionParams[]>();
const readyProviders = new Set<ConversionProviderId>();

function appendToPendingQueue(id: ConversionProviderId, params: FireConversionParams): void {
  const existing = pendingByProvider.get(id);
  if (existing === undefined) {
    pendingByProvider.set(id, [params]);
    return;
  }
  existing.push(params);
}

function safeDispatch(provider: RegisteredProvider, params: FireConversionParams): void {
  try {
    provider.dispatch(params);
  } catch (error: unknown) {
    registryLogger.warn("provider dispatch threw; swallowing", {
      error,
      providerId: provider.id,
      eventKey: params.eventKey,
      eventId: params.eventId,
    });
  }
}

/** Last registration wins — test resets rely on this. */
export function registerConversionProvider(provider: RegisteredProvider): void {
  providers.set(provider.id, provider);
}

/**
 * Fan out to every provider; queue for any provider not yet ready.
 * Queued events are flushed in FIFO order on `markConversionProviderReady`.
 * Dedup is the caller's responsibility via `eventId`.
 */
export function dispatchConversionToAllProviders(params: FireConversionParams): void {
  for (const [id, provider] of providers) {
    if (!readyProviders.has(id)) {
      appendToPendingQueue(id, params);
      continue;
    }
    safeDispatch(provider, params);
  }
}

/** Flush the pending queue for `id` in FIFO order. Idempotent. */
export function markConversionProviderReady(id: ConversionProviderId): void {
  readyProviders.add(id);
  const provider = providers.get(id);
  if (provider === undefined) {
    return;
  }
  const queued = pendingByProvider.get(id) ?? [];
  pendingByProvider.delete(id);
  for (const params of queued) {
    safeDispatch(provider, params);
  }
}

/** Drop queued events without dispatching — for providers disabled at runtime. */
export function clearPendingConversionsForProvider(id: ConversionProviderId): void {
  pendingByProvider.delete(id);
}

/** Test-only reset. */
export function resetConversionRegistryForTests(): void {
  providers.clear();
  pendingByProvider.clear();
  readyProviders.clear();
}
